import { Big } from "big.js";
import * as z from "zod/v4";
import { ZodTypeAny } from "zod/v4";

export function big(params?: z.core.$ZodCustomParams) {
  return z
    .custom<Big.Big>(
      (data) =>
        data instanceof Big ||
        typeof data === "string" ||
        typeof data === "number",
      params,
    )
    .transform<Big.Big>(Big);
}

export function instant(params?: z.core.$ZodCustomParams) {
  return z.custom<Temporal.Instant>(
    (data) => data instanceof Temporal.Instant,
    params,
  );
}

export function plainDate(params?: z.core.$ZodCustomParams) {
  return z
    .custom<Temporal.PlainDate>((data) => {
      if (data instanceof Temporal.PlainDate) {
        return true;
      }
      if (typeof data === "string") {
        try {
          Temporal.PlainDate.from(data);
          return true;
        } catch (e) {
          // it isn't a valid date string
        }
      }
      return false;
    }, params)
    .transform<Temporal.PlainDate>((data) => Temporal.PlainDate.from(data));
}

export function plainTime(params?: z.core.$ZodCustomParams) {
  return z
    .custom<Temporal.PlainTime>((data) => {
      if (data instanceof Temporal.PlainTime) {
        return true;
      }
      if (typeof data === "string") {
        try {
          Temporal.PlainTime.from(data);
          return true;
        } catch (e) {
          // it isn't a valid time string
        }
      }
      return false;
    }, params)
    .transform<Temporal.PlainTime>((data) => Temporal.PlainTime.from(data));
}

/**
 * Takes a Zod schema and returns a Zod schema that parses arrays,
 * filtering out invalid items and only returning valid ones.
 */
export function filterToValidArray<T extends ZodTypeAny>(itemSchema: T) {
  return z.array(z.any()).transform((items) => {
    return items
      .map((item) => itemSchema.safeParse(item))
      .filter(
        (result): result is z.ZodSafeParseSuccess<z.infer<T>> => result.success,
      )
      .map((result) => result.data);
  });
}

export function filterToValid<T extends ZodTypeAny>(itemSchema: T) {
  return z.any().transform((item) => {
    return itemSchema.safeParse(item).data;
  });
}

/**
 * @param ref - Referenced collection name for FK generation (e.g., "teams", "orders")
 */
export function objectId(ref?: string, params?: z.core.$ZodCustomParams) {
  return z
    .custom<string>(
      (id) =>
        typeof id === "string" ||
        (typeof id === "object" && id && "toString" in id),
      params,
    )
    .transform<string>((id) => (typeof id === "string" ? id : String(id)))
    .meta({ annotation: "ObjectId", ...(ref && { ref }) });
}

export function coercedResult<T extends ZodTypeAny>(okType: T) {
  return z.union([
    z.object({ ok: z.literal(true), value: okType }),
    z.object({
      ok: z.literal(false),
      error: z.object({
        message: z.string(),
        name: z.string(),
        stack: z.optional(z.string()),
      }),
    }),
  ]);
}

/**
 * Recursively converts every `.optional()` in a schema to
 * required + nullable so that no field can be `undefined`
 * (OpenAI‑JSON compliant) but all may be `null`.
 *
 * From https://github.com/colinhacks/zod/discussions/2050#discussioncomment-13818536
 */
export function optionalToNullable<S extends z.ZodTypeAny>(schema: S): S {
  if (schema instanceof z.ZodOptional) {
    return optionalToNullable(
      schema.unwrap() as any,
    ).nullable() as unknown as S;
  }

  if (schema instanceof z.ZodObject) {
    const transformed = Object.fromEntries(
      Object.entries(schema.shape).map(([k, v]) => [k, optionalToNullable(v)]),
    );
    return z.object(transformed) as unknown as S;
  }

  if (schema instanceof z.ZodArray) {
    return z.array(optionalToNullable(schema.element as any)) as unknown as S;
  }
  if (schema instanceof z.ZodRecord) {
    return z.record(
      z.string(),
      optionalToNullable(schema.valueType as any),
    ) as unknown as S;
  }
  if (schema instanceof z.ZodTuple) {
    return z.tuple(
      schema.def.items.map((x) => optionalToNullable(x as any)) as any,
    ) as unknown as S;
  }
  if (schema instanceof z.ZodUnion) {
    return z.union(
      schema.def.options.map((x) => optionalToNullable(x as any)),
    ) as unknown as S;
  }
  if (schema instanceof z.ZodIntersection) {
    return z.intersection(
      optionalToNullable(schema.def.left as any),
      optionalToNullable(schema.def.right as any),
    ) as unknown as S;
  }

  if (schema instanceof z.ZodMap) {
    return z.map(
      optionalToNullable(schema.def.keyType as any),
      optionalToNullable(schema.def.valueType as any),
    ) as unknown as S;
  }
  if (schema instanceof z.ZodSet) {
    return z.set(
      optionalToNullable(schema.def.valueType as any),
    ) as unknown as S;
  }
  if (schema instanceof z.ZodPromise) {
    return z.promise(
      optionalToNullable(schema.def.type as any),
    ) as unknown as S;
  }

  return schema;
}

/** Allow parsing when field is missing, but require it at the Typescript-level */
export function coerceNull<T extends z.ZodTypeAny>(schema: T) {
  return schema.nullish().transform((val) => val ?? null);
}

export function numericString() {
  return z
    .string()
    .refine((val) => /^[0-9]+$/.test(val), {
      message: "Must contain only digits",
    })
    .transform((x) => x as `${number}`);
}
